import api from './api'
import type {
  SkillSearchResponse,
  SkillCategoriesResponse,
  SkillDetailsResponse,
  SkillSearchParams,
  AddCandidateSkillRequest,
  AddCandidateSkillResponse,
  UpdateCandidateSkillRequest,
  UpdateCandidateSkillResponse
} from '@/types'

/**
 * Search for skills using autocomplete
 * @param params - Search parameters (query, category, limit)
 * @returns Promise<SkillSearchResponse>
 */
export const searchSkills = async (params: SkillSearchParams): Promise<SkillSearchResponse> => {
  const { q, category, limit = 20 } = params

  const queryParams = new URLSearchParams({
    q,
    ...(category && { category }),
    limit: limit.toString(),
  })

  const response = await api.get(`/skills/search?${queryParams.toString()}`)
  return response.data
}

/**
 * Get all available skill categories
 * @returns Promise<SkillCategoriesResponse>
 */
export const getSkillCategories = async (): Promise<SkillCategoriesResponse> => {
  const response = await api.get('/skills/categories')
  return response.data
}

/**
 * Get detailed information about a specific skill
 * @param skillId - The skill ID
 * @returns Promise<SkillDetailsResponse>
 */
export const getSkillById = async (skillId: string): Promise<SkillDetailsResponse> => {
  const response = await api.get(`/skills/${skillId}`)
  return response.data
}

/**
 * Add a skill to candidate profile
 * @param data - Skill data { skillId, proficiency }
 * @returns Promise with skill details { id, skillId, skillName, skillCategory, proficiency }
 */
export const addCandidateSkill = async (
  data: AddCandidateSkillRequest
): Promise<AddCandidateSkillResponse> => {
  const response = await api.post('/candidate/profile/skill', data)
  return response.data
}

/**
 * Update skill proficiency in candidate profile
 * @param skillId - The candidate skill record ID
 * @param data - Update data { proficiency }
 * @returns Promise with updated skill details
 */
export const updateCandidateSkill = async (
  skillId: string,
  data: UpdateCandidateSkillRequest
): Promise<UpdateCandidateSkillResponse> => {
  const response = await api.patch(`/candidate/profile/skill/${skillId}`, data)
  return response.data
}

/**
 * Delete a skill from candidate profile
 * @param skillId - The skill ID to delete
 */
export const deleteCandidateSkill = async (skillId: string) => {
  const response = await api.delete(`/candidate/profile/skill/${skillId}`)
  return response.data
}

export const skillsService = {
  searchSkills,
  getSkillCategories,
  getSkillById,
  addCandidateSkill,
  updateCandidateSkill,
  deleteCandidateSkill,
}
